import React, { useEffect, useRef, useState } from "react";
import { ABtn, AResult } from "./arcadeUi";
import { sfx } from "@/lib/soundfx";

const CELL = 22, DIM = 15 * CELL; // 330

// clockwise 52-cell main track as [col,row] on a 15x15 grid
const PATH = [
  [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 5], [6, 4], [6, 3], [6, 2], [6, 1], [6, 0], [7, 0],
  [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [14, 6],
  [14, 7], [14, 8], [13, 8], [12, 8], [11, 8], [10, 8], [9, 8], [8, 9], [8, 10], [8, 11], [8, 12], [8, 13],
  [8, 14], [7, 14], [6, 14], [6, 13], [6, 12], [6, 11], [6, 10], [6, 9], [5, 8], [4, 8], [3, 8], [2, 8],
  [1, 8], [0, 8], [0, 7], [0, 6],
];
const STARTS = [0, 13, 26, 39];
const SAFE = new Set([0, 13, 26, 39, 8, 21, 34, 47]);
const HOME = [
  [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [7, 7]],
  [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 7]],
  [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [7, 7]],
  [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 7]],
];
const BASE = [
  [[1.5, 1.5], [3.5, 1.5], [1.5, 3.5], [3.5, 3.5]],
  [[10.5, 1.5], [12.5, 1.5], [10.5, 3.5], [12.5, 3.5]],
  [[10.5, 10.5], [12.5, 10.5], [10.5, 12.5], [12.5, 12.5]],
  [[1.5, 10.5], [3.5, 10.5], [1.5, 12.5], [3.5, 12.5]],
];
const COLORS = ["#e63946", "#2a9d8f", "#e9b949", "#457b9d"];
const NAMES = ["You", "Green", "Yellow", "Blue"];
const isCross = (c, r) => (c >= 6 && c <= 8) || (r >= 6 && r <= 8);
const cellCoord = (player, i, p) => {
  if (p === 0) return BASE[player][i];
  if (p <= 51) return PATH[(STARTS[player] + p - 1) % 52];
  return HOME[player][p - 52];
};
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export default function Ludo() {
  const canvasRef = useRef(null);
  const eng = useRef({ players: [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]], turn: 0, dice: null, selectable: [], winner: null });
  const rollRes = useRef(null);
  const moveRes = useRef(null);
  const started = useRef(false);
  const [ui, setUi] = useState({ turn: 0, dice: null, winner: null, msg: "Press Roll to start!", canRoll: false, selectable: [] });

  const sync = (extra = {}) => setUi((u) => ({ ...u, turn: eng.current.turn, dice: eng.current.dice, winner: eng.current.winner, selectable: eng.current.selectable.slice(), ...extra }));
  const setMsg = (msg) => setUi((u) => ({ ...u, msg }));

  const draw = () => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext("2d");
    ctx.fillStyle = "#f6efdd"; ctx.fillRect(0, 0, DIM, DIM);
    // cross cells
    for (let r = 0; r < 15; r++) for (let c = 0; c < 15; c++) {
      if (isCross(c, r)) { ctx.fillStyle = "#fff"; ctx.fillRect(c * CELL, r * CELL, CELL, CELL); ctx.strokeStyle = "#d9c48a"; ctx.lineWidth = 1; ctx.strokeRect(c * CELL, r * CELL, CELL, CELL); }
    }
    // base squares
    const corners = [[0, 0], [9, 0], [9, 9], [0, 9]];
    corners.forEach(([bc, br], p) => {
      ctx.fillStyle = COLORS[p]; ctx.fillRect(bc * CELL, br * CELL, 6 * CELL, 6 * CELL);
      ctx.fillStyle = "#fff"; ctx.fillRect((bc + 1) * CELL, (br + 1) * CELL, 4 * CELL, 4 * CELL);
      BASE[p].forEach(([sx, sy]) => { ctx.fillStyle = COLORS[p]; ctx.beginPath(); ctx.arc(sx * CELL, sy * CELL, CELL * 0.55, 0, 7); ctx.fill(); ctx.fillStyle = "#fff"; ctx.beginPath(); ctx.arc(sx * CELL, sy * CELL, CELL * 0.32, 0, 7); ctx.fill(); });
    });
    // colored start + home cells
    for (let p = 0; p < 4; p++) {
      const [sc, sr] = PATH[STARTS[p]]; ctx.fillStyle = COLORS[p]; ctx.globalAlpha = 0.85; ctx.fillRect(sc * CELL, sr * CELL, CELL, CELL); ctx.globalAlpha = 1;
      HOME[p].slice(0, 5).forEach(([hc, hr]) => { ctx.fillStyle = COLORS[p]; ctx.fillRect(hc * CELL, hr * CELL, CELL, CELL); ctx.strokeStyle = "#fff"; ctx.strokeRect(hc * CELL, hr * CELL, CELL, CELL); });
    }
    // center triangles
    const X0 = 6 * CELL, Y0 = 6 * CELL, X1 = 9 * CELL, Y1 = 9 * CELL, cx = 7.5 * CELL, cy = 7.5 * CELL;
    const tri = (ax, ay, bx, by, col) => { ctx.fillStyle = col; ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.lineTo(cx, cy); ctx.closePath(); ctx.fill(); };
    tri(X0, Y0, X1, Y0, COLORS[1]); tri(X1, Y0, X1, Y1, COLORS[2]); tri(X1, Y1, X0, Y1, COLORS[3]); tri(X0, Y1, X0, Y0, COLORS[0]);
    // tokens (with cluster offset)
    const occ = {};
    for (let p = 0; p < 4; p++) for (let i = 0; i < 4; i++) {
      const prog = eng.current.players[p][i];
      const [c, r] = cellCoord(p, i, prog);
      const key = prog === 0 ? `b${p}${i}` : `${Math.round(c)}-${Math.round(r)}`;
      occ[key] = occ[key] || []; occ[key].push([p, i]);
    }
    Object.values(occ).forEach((list) => {
      list.forEach(([p, i], k) => {
        const prog = eng.current.players[p][i];
        const [c, r] = cellCoord(p, i, prog);
        const baseX = prog === 0 ? c * CELL : (c + 0.5) * CELL;
        const baseY = prog === 0 ? r * CELL : (r + 0.5) * CELL;
        const off = list.length > 1 ? (k - (list.length - 1) / 2) * 6 : 0;
        const sel = p === 0 && eng.current.selectable.includes(i);
        ctx.fillStyle = COLORS[p];
        ctx.beginPath(); ctx.arc(baseX + off, baseY, 7, 0, 7); ctx.fill();
        ctx.strokeStyle = sel ? "#111" : "#fff"; ctx.lineWidth = sel ? 3 : 2; ctx.stroke();
        if (sel) { ctx.strokeStyle = "#111"; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(baseX + off, baseY, 10, 0, 7); ctx.stroke(); }
      });
    });
  };

  useEffect(() => { draw(); }); // redraw on every render

  const getMovable = (player, dice) => {
    const out = [];
    eng.current.players[player].forEach((p, i) => {
      if (p === 0) { if (dice === 6) out.push(i); }
      else if (p + dice <= 57) out.push(i);
    });
    return out;
  };

  const applyMove = (player, i, dice) => {
    const p = eng.current.players[player][i];
    const np = p === 0 ? 1 : p + dice;
    eng.current.players[player][i] = np;
    let captured = false;
    if (np >= 1 && np <= 51) {
      const abs = (STARTS[player] + np - 1) % 52;
      if (!SAFE.has(abs)) {
        for (let op = 0; op < 4; op++) {
          if (op === player) continue;
          eng.current.players[op].forEach((opp, j) => {
            if (opp >= 1 && opp <= 51 && (STARTS[op] + opp - 1) % 52 === abs) { eng.current.players[op][j] = 0; captured = true; }
          });
        }
      }
    }
    return { captured, home: np === 57 };
  };

  const checkWin = (player) => eng.current.players[player].every((p) => p === 57);

  const cpuChoose = (player, movable, dice) => {
    let best = movable[0], bestScore = -Infinity;
    for (const i of movable) {
      const p = eng.current.players[player][i];
      const np = p === 0 ? 1 : p + dice;
      let score = np * 0.5;
      if (p === 0) score += 25;
      if (np === 57) score += 60;
      if (np <= 51) {
        const abs = (STARTS[player] + np - 1) % 52;
        if (!SAFE.has(abs)) for (let op = 0; op < 4; op++) {
          if (op === player) continue;
          eng.current.players[op].forEach((opp) => { if (opp >= 1 && opp <= 51 && (STARTS[op] + opp - 1) % 52 === abs) score += 100; });
        }
      }
      if (score > bestScore) { bestScore = score; best = i; }
    }
    return best;
  };

  const waitHumanRoll = () => new Promise((res) => { rollRes.current = res; setUi((u) => ({ ...u, canRoll: true })); });
  const waitHumanMove = () => new Promise((res) => { moveRes.current = res; });

  const onRoll = () => { if (rollRes.current) { const r = rollRes.current; rollRes.current = null; setUi((u) => ({ ...u, canRoll: false })); r(); } };

  const onBoardClick = (e) => {
    if (!moveRes.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = Math.floor(((e.clientX - rect.left) / rect.width) * 15);
    const cy = Math.floor(((e.clientY - rect.top) / rect.height) * 15);
    for (const i of eng.current.selectable) {
      const prog = eng.current.players[0][i];
      const [c, r] = cellCoord(0, i, prog);
      if (Math.floor(c) === cx && Math.floor(r) === cy) { const res = moveRes.current; moveRes.current = null; res(i); return; }
    }
  };

  const play = async () => {
    while (eng.current.winner === null) {
      const player = eng.current.turn;
      if (player === 0) { setMsg("Your turn — press Roll"); await waitHumanRoll(); }
      else { setMsg(`${NAMES[player]} is rolling…`); await sleep(600); }
      const dice = 1 + Math.floor(Math.random() * 6);
      eng.current.dice = dice; sync(); sfx.click(); await sleep(450);
      const movable = getMovable(player, dice);
      let extra = false;
      if (movable.length) {
        let idx;
        if (player === 0) {
          if (movable.length === 1) { idx = movable[0]; }
          else { eng.current.selectable = movable; sync(); setMsg("Tap a highlighted token to move"); idx = await waitHumanMove(); eng.current.selectable = []; sync(); }
        } else { idx = cpuChoose(player, movable, dice); await sleep(550); }
        const { captured, home } = applyMove(player, idx, dice);
        if (captured) sfx.explode(); else if (home) sfx.powerUp(); else sfx.beep();
        sync();
        if (checkWin(player)) { eng.current.winner = player; sync(); sfx.win(); break; }
        extra = dice === 6 || captured || home;
        if (extra) setMsg(`${NAMES[player]} rolled ${dice} — bonus turn!`);
      } else {
        setMsg(`${NAMES[player]} rolled ${dice} — no move`); await sleep(600);
      }
      eng.current.dice = null;
      if (!extra) eng.current.turn = (player + 1) % 4;
      sync();
    }
  };

  const start = () => {
    eng.current = { players: [[0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0], [0, 0, 0, 0]], turn: 0, dice: null, selectable: [], winner: null };
    rollRes.current = null; moveRes.current = null;
    setUi({ turn: 0, dice: null, winner: null, msg: "Your turn — press Roll", canRoll: false, selectable: [] });
    started.current = true;
    play();
  };

  useEffect(() => { start(); return () => { rollRes.current = null; moveRes.current = null; }; }, []); // eslint-disable-line

  return (
    <div className="w-full text-center">
      <div className="mb-2 flex flex-wrap items-center justify-center gap-1.5">
        {NAMES.map((n, i) => (
          <span key={i} data-testid={`ludo-tag-${i}`} className={`flex items-center gap-1 rounded-md border-2 px-2 py-0.5 text-xs font-bold ${ui.turn === i && ui.winner === null ? "border-kirana-brown bg-kirana-gold" : "border-kirana-border bg-white"}`}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} /> {n}
          </span>
        ))}
      </div>
      <div className="relative mx-auto w-full" style={{ maxWidth: DIM }}>
        <canvas ref={canvasRef} width={DIM} height={DIM} data-testid="ludo-board" onClick={onBoardClick} className="w-full rounded-md border-[3px] border-kirana-brown" />
        {ui.winner !== null && (
          <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/55 p-3">
            <AResult win={ui.winner === 0} text={ui.winner === 0 ? "You Win! 🏆" : `${NAMES[ui.winner]} Wins 😜`} testId="ludo-over" />
            <ABtn tone="green" onClick={start} className="mt-3" data-testid="ludo-again">Play Again</ABtn>
          </div>
        )}
      </div>
      <p className="mt-2 min-h-[20px] text-sm font-bold text-kirana-brown" data-testid="ludo-msg">{ui.msg}</p>
      <div className="mt-1 flex items-center justify-center gap-3">
        <div data-testid="ludo-dice" className="flex h-12 w-12 items-center justify-center rounded-md border-[3px] border-kirana-brown bg-white font-signboard text-2xl font-bold text-kirana-red">{ui.dice ?? "—"}</div>
        <ABtn tone="red" data-testid="ludo-roll" disabled={!ui.canRoll} onClick={onRoll}>Roll Dice</ABtn>
      </div>
    </div>
  );
}
